import React from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAppDispatch } from '../../../../support/store';
import {
  addJourneyMapDraftPathGroupItem,
  removeJourneyMapDraftPathGroup,
  removeJourneyMapDraftPathGroupItem,
  updateJourneyMapDraftPathGroup,
} from '../../../../support/slices/journeyMapBuilderSlice';
import {
  JourneyMapDraftItem,
  JourneyMapDraftPathGroup,
} from '../../../../types/journeyMap';

type Props = {
  pathGroup: JourneyMapDraftPathGroup;
  availableItems: JourneyMapDraftItem[];
};

const JourneyMapSidePanelPathGroupEditor: React.FC<Props> = ({
                                                               pathGroup,
                                                               availableItems,
                                                             }) => {
  const dispatch = useAppDispatch();

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Path Group
        </Typography>

        <TextField
          label="Name"
          size="small"
          value={pathGroup.name}
          onChange={(e) =>
            dispatch(
              updateJourneyMapDraftPathGroup({
                tempId: pathGroup.tempId,
                changes: { name: e.target.value },
              }),
            )
          }
          fullWidth
        />

        <TextField
          label="Description"
          size="small"
          value={pathGroup.description || ''}
          onChange={(e) =>
            dispatch(
              updateJourneyMapDraftPathGroup({
                tempId: pathGroup.tempId,
                changes: { description: e.target.value },
              }),
            )
          }
          fullWidth
          multiline
          minRows={3}
        />

        <FormControl size="small" fullWidth>
          <InputLabel id="group-type-label">Group Type</InputLabel>
          <Select
            labelId="group-type-label"
            label="Group Type"
            value={pathGroup.group_type || 'all_of'}
            onChange={(e) =>
              dispatch(
                updateJourneyMapDraftPathGroup({
                  tempId: pathGroup.tempId,
                  changes: { group_type: e.target.value as any },
                }),
              )
            }
          >
            <MenuItem value="all_of">All Of</MenuItem>
            <MenuItem value="any_of">Any Of</MenuItem>
            <MenuItem value="one_of">One Of</MenuItem>
            <MenuItem value="optional">Optional</MenuItem>
            <MenuItem value="test_out">Test Out</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Minimum Required"
          size="small"
          type="number"
          value={pathGroup.min_required ?? 1}
          onChange={(e) =>
            dispatch(
              updateJourneyMapDraftPathGroup({
                tempId: pathGroup.tempId,
                changes: { min_required: Number(e.target.value) || 1 },
              }),
            )
          }
          fullWidth
        />

        <Divider />

        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Group Items
        </Typography>

        <FormControl size="small" fullWidth>
          <InputLabel id="add-group-item-label">Add Item</InputLabel>
          <Select
            labelId="add-group-item-label"
            label="Add Item"
            value=""
            onChange={(e) => {
              const itemTempId = String(e.target.value);
              if (!itemTempId) return;

              dispatch(
                addJourneyMapDraftPathGroupItem({
                  pathGroupTempId: pathGroup.tempId,
                  journeyMapItemTempId: itemTempId,
                }),
              );
            }}
          >
            {availableItems.map((item) => (
              <MenuItem key={item.tempId} value={item.tempId}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack spacing={1}>
          {pathGroup.items
            .filter((groupItem) => !groupItem.isDeleted)
            .map((groupItem) => {
              const linkedItem = availableItems.find(
                (item) => item.tempId === groupItem.journey_map_item_temp_id,
              );

              return (
                <Box
                  key={groupItem.tempId}
                  sx={{
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2">
                    {linkedItem?.name || 'Unknown Item'}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() =>
                      dispatch(
                        removeJourneyMapDraftPathGroupItem({
                          pathGroupTempId: pathGroup.tempId,
                          itemTempId: groupItem.tempId,
                        }),
                      )
                    }
                  >
                    Remove
                  </Button>
                </Box>
              );
            })}
        </Stack>

        <Button
          color="error"
          variant="contained"
          onClick={() => dispatch(removeJourneyMapDraftPathGroup(pathGroup.tempId))}
        >
          Delete Path Group
        </Button>
      </Stack>
    </Box>
  );
};

export default JourneyMapSidePanelPathGroupEditor;